import { useState, useCallback, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronLeft, ChevronRight, Upload, Car, Zap, Check, Loader2, Clock } from "lucide-react";
import { addDays, format, startOfDay } from "date-fns";
import { sv } from "date-fns/locale";
import api from "@/lib/api";

const STEPS = ["Modell & datum", "Uppgifter", "Körkort", "Hyresvillkor", "Bekräfta"];
const TIME_SLOTS = ["08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00"];
const DELIVERY_PRICE = 300;

export default function BookingModal({ open, onOpenChange }) {
    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [terms, setTerms] = useState(null);
    const [formData, setFormData] = useState({
        model: "", date: null, time: "10:00", name: "", email: "", phone: "",
        delivery: "pickup", licenseFile: null, termsAccepted: false,
    });

    const minDate = addDays(startOfDay(new Date()), 2);
    const deliveryCost = formData.delivery === "delivery" ? DELIVERY_PRICE : 0;
    const totalPrice = 500 + deliveryCost;

    useEffect(() => {
        if (open && !terms) {
            api.get("/rental-terms").then((r) => setTerms(r.data)).catch(() => {});
        }
    }, [open, terms]);

    const updateField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setError("");
    };

    const canProceed = useCallback(() => {
        switch (step) {
            case 0: return formData.model && formData.date && formData.time;
            case 1: return formData.name && formData.email && formData.phone;
            case 2: return formData.licenseFile;
            case 3: return formData.termsAccepted;
            case 4: return true;
            default: return false;
        }
    }, [step, formData]);

    const handleSubmit = async () => {
        setLoading(true);
        setError("");
        try {
            const bookingRes = await api.post("/bookings", {
                date: formData.date.toISOString().split("T")[0],
                time: formData.time,
                model: formData.model,
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                delivery: formData.delivery,
                terms_accepted: formData.termsAccepted,
            });
            const bookingId = bookingRes.data.id;
            const fd = new FormData();
            fd.append("file", formData.licenseFile);
            fd.append("booking_id", bookingId);
            await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const checkoutRes = await api.post("/checkout", { booking_id: bookingId, origin_url: window.location.origin });
            window.location.href = checkoutRes.data.url;
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === "string" ? detail : "Något gick fel. Försök igen.");
            setLoading(false);
        }
    };

    const resetAndClose = () => {
        setStep(0);
        setFormData({ model: "", date: null, time: "10:00", name: "", email: "", phone: "", delivery: "pickup", licenseFile: null, termsAccepted: false });
        setError("");
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={resetAndClose}>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto p-0" data-testid="booking-modal">
                <DialogHeader className="p-6 pb-0">
                    <DialogTitle className="text-xl font-medium tracking-tight">Boka provkörning</DialogTitle>
                    <div className="flex gap-1 mt-4">
                        {STEPS.map((s, i) => (
                            <div key={i} className="flex-1 flex flex-col gap-1">
                                <div className={`h-1 rounded-full transition-colors ${i <= step ? "bg-black" : "bg-neutral-200"}`} />
                                <span className={`text-[9px] uppercase tracking-wider ${i <= step ? "text-black" : "text-neutral-400"}`}>{s}</span>
                            </div>
                        ))}
                    </div>
                </DialogHeader>

                <div className="p-6">
                    {error && <div data-testid="booking-error" className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">{error}</div>}

                    {/* Step 0: Model, Date & Time */}
                    {step === 0 && (
                        <div className="space-y-5">
                            <div>
                                <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-3 block">Välj modell</Label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button data-testid="model-select-small" onClick={() => updateField("model", "small")}
                                        className={`p-4 border text-left transition-all ${formData.model === "small" ? "border-black bg-neutral-50" : "border-neutral-200 hover:border-neutral-400"}`}>
                                        <Car className="mb-2 text-neutral-600" size={20} />
                                        <div className="font-medium text-sm">Birò Small</div>
                                        <div className="text-xs text-neutral-500 mt-1">Mopedklass · 45 km/h</div>
                                    </button>
                                    <button data-testid="model-select-big" onClick={() => updateField("model", "big")}
                                        className={`p-4 border text-left transition-all ${formData.model === "big" ? "border-black bg-neutral-50" : "border-neutral-200 hover:border-neutral-400"}`}>
                                        <Zap className="mb-2 text-neutral-600" size={20} />
                                        <div className="font-medium text-sm">Birò Big</div>
                                        <div className="text-xs text-neutral-500 mt-1">B-körkort · 60 km/h</div>
                                    </button>
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-3 block">Välj datum (minst 48h framåt)</Label>
                                <Calendar data-testid="booking-calendar" mode="single" selected={formData.date} onSelect={(d) => updateField("date", d)}
                                    disabled={{ before: minDate }} fromDate={minDate} locale={sv} className="rounded-md border border-neutral-200" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2 block">Klockslag för upphämtning/leverans</Label>
                                <Select value={formData.time} onValueChange={(v) => updateField("time", v)}>
                                    <SelectTrigger data-testid="time-select" className="w-full">
                                        <Clock size={14} className="mr-2 text-neutral-400" />
                                        <SelectValue placeholder="Välj tid" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {TIME_SLOTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}

                    {/* Step 1: Personal Info & Delivery */}
                    {step === 1 && (
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="name" className="text-xs uppercase tracking-[0.15em] text-neutral-500">Namn</Label>
                                <Input data-testid="booking-name" id="name" value={formData.name} onChange={(e) => updateField("name", e.target.value)} placeholder="Förnamn Efternamn" className="mt-1.5" />
                            </div>
                            <div>
                                <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] text-neutral-500">E-post</Label>
                                <Input data-testid="booking-email" id="email" type="email" value={formData.email} onChange={(e) => updateField("email", e.target.value)} placeholder="namn@exempel.se" className="mt-1.5" />
                            </div>
                            <div>
                                <Label htmlFor="phone" className="text-xs uppercase tracking-[0.15em] text-neutral-500">Telefon</Label>
                                <Input data-testid="booking-phone" id="phone" value={formData.phone} onChange={(e) => updateField("phone", e.target.value)} placeholder="07X XXX XX XX" className="mt-1.5" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2 block">Leverans</Label>
                                <div className="grid grid-cols-1 gap-3">
                                    <button data-testid="delivery-pickup" onClick={() => updateField("delivery", "pickup")}
                                        className={`p-3 border text-left text-sm ${formData.delivery === "pickup" ? "border-black bg-neutral-50" : "border-neutral-200"}`}>
                                        <span className="font-medium">Upphämtning i Nacka</span>
                                        <span className="text-neutral-500 ml-2">Ingår</span>
                                    </button>
                                    <button data-testid="delivery-deliver" onClick={() => updateField("delivery", "delivery")}
                                        className={`p-3 border text-left text-sm ${formData.delivery === "delivery" ? "border-black bg-neutral-50" : "border-neutral-200"}`}>
                                        <span className="font-medium">Leverans Stockholm</span>
                                        <span className="text-neutral-500 ml-2">(inom tullarna) +300 kr</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: License Upload */}
                    {step === 2 && (
                        <div className="space-y-4">
                            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Ladda upp körkort</Label>
                            <div className="border-2 border-dashed border-neutral-300 rounded-md p-8 text-center hover:border-neutral-400 transition-colors">
                                <Upload className="mx-auto mb-3 text-neutral-400" size={32} />
                                <p className="text-sm text-neutral-600 mb-3">Klicka för att välja fil (bild eller PDF)</p>
                                <input data-testid="license-upload" type="file" accept="image/*,.pdf" className="hidden" id="license-input"
                                    onChange={(e) => updateField("licenseFile", e.target.files?.[0] || null)} />
                                <Button variant="outline" size="sm" onClick={() => document.getElementById("license-input").click()}>Välj fil</Button>
                            </div>
                            {formData.licenseFile && (
                                <div className="flex items-center gap-2 p-3 bg-neutral-50 border border-neutral-200">
                                    <Check size={16} className="text-green-600" />
                                    <span className="text-sm truncate">{formData.licenseFile.name}</span>
                                    <Badge variant="secondary" className="ml-auto text-xs">{(formData.licenseFile.size / 1024).toFixed(0)} KB</Badge>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 3: Rental Terms */}
                    {step === 3 && (
                        <div className="space-y-4">
                            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Hyresvillkor</Label>
                            <ScrollArea className="h-64 border border-neutral-200 rounded-md p-4" data-testid="rental-terms-scroll">
                                {terms?.content?.map((section, i) => (
                                    <div key={i} className="mb-4">
                                        <h4 className="text-sm font-medium mb-1">{section.heading}</h4>
                                        <p className="text-xs text-neutral-600 leading-relaxed">{section.text}</p>
                                    </div>
                                ))}
                                {!terms && <p className="text-sm text-neutral-400">Laddar hyresvillkor...</p>}
                            </ScrollArea>
                            <div className="flex items-start gap-3 p-3 border border-neutral-200 rounded-md">
                                <Checkbox id="accept-terms" data-testid="accept-terms-checkbox"
                                    checked={formData.termsAccepted} onCheckedChange={(c) => updateField("termsAccepted", !!c)} />
                                <label htmlFor="accept-terms" className="text-sm leading-relaxed cursor-pointer">
                                    Jag har läst och accepterar hyresvillkoren
                                </label>
                            </div>
                        </div>
                    )}

                    {/* Step 4: Review & Pay */}
                    {step === 4 && (
                        <div className="space-y-4">
                            <div className="bg-neutral-50 p-4 space-y-3">
                                <div className="flex justify-between text-sm"><span className="text-neutral-500">Modell</span><span className="font-medium">Birò {formData.model === "small" ? "Small" : "Big"}</span></div>
                                <div className="flex justify-between text-sm"><span className="text-neutral-500">Datum</span><span className="font-medium">{formData.date ? format(formData.date, "d MMMM yyyy", { locale: sv }) : "-"}</span></div>
                                <div className="flex justify-between text-sm"><span className="text-neutral-500">Tid</span><span className="font-medium">{formData.time}</span></div>
                                <div className="flex justify-between text-sm"><span className="text-neutral-500">Namn</span><span className="font-medium">{formData.name}</span></div>
                                <div className="flex justify-between text-sm"><span className="text-neutral-500">E-post</span><span className="font-medium">{formData.email}</span></div>
                                <div className="flex justify-between text-sm"><span className="text-neutral-500">Telefon</span><span className="font-medium">{formData.phone}</span></div>
                                <div className="flex justify-between text-sm"><span className="text-neutral-500">Leverans</span><span className="font-medium">{formData.delivery === "pickup" ? "Nacka" : "Stockholm (inom tullarna)"}</span></div>
                            </div>
                            <div className="border-t pt-4 space-y-2">
                                <div className="flex justify-between text-sm"><span className="text-neutral-500">Provkörning (24 timmar)</span><span>500 kr</span></div>
                                {deliveryCost > 0 && <div className="flex justify-between text-sm"><span className="text-neutral-500">Leverans Stockholm</span><span>300 kr</span></div>}
                                <div className="flex justify-between items-center pt-2 border-t"><span className="font-medium">Totalt</span><span className="text-2xl font-medium tracking-tight">{totalPrice} kr</span></div>
                            </div>
                            <p className="text-xs text-neutral-400">Du omdirigeras till Stripe för säker betalning. Vi bekräftar din bokning inom 24 timmar.</p>
                        </div>
                    )}

                    {/* Navigation */}
                    <div className="flex justify-between mt-6 pt-4 border-t border-neutral-100">
                        {step > 0 ? (
                            <Button variant="ghost" onClick={() => setStep(step - 1)} data-testid="booking-back-btn">
                                <ChevronLeft size={16} className="mr-1" /> Tillbaka
                            </Button>
                        ) : <div />}
                        {step < 4 ? (
                            <Button data-testid="booking-next-btn" onClick={() => setStep(step + 1)} disabled={!canProceed()} className="bg-black text-white hover:bg-neutral-800">
                                Nästa <ChevronRight size={16} className="ml-1" />
                            </Button>
                        ) : (
                            <Button data-testid="booking-pay-btn" onClick={handleSubmit} disabled={loading} className="bg-black text-white hover:bg-neutral-800">
                                {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                                {loading ? "Bearbetar..." : `Betala ${totalPrice} kr`}
                            </Button>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
